import React from 'react';
import './PlayerSettings.css';
import languageOptions from './languageOptions';

function PlayerSettings({
  playerSettings,
  onSettingChange,
  fadeDuration,
  handleFadeDurationChange,
  watermarkSize,
  watermarkPosition,
  youtubePlayerCompatibilityMode,
  onWatermarkSizeChange,
  onWatermarkPositionChange,
  onYoutubePlayerCompatibilityModeChange,
  pauseBackgroundVideoOnVideoOverlay = false,
  overlayPauseSettingLoading = false,
  overlayPauseSettingSaving = false,
  overlayPauseSettingError,
  overlayPauseSettingWarning,
  overlayPauseSettingLoadFailed = false,
  onVideoOverlayPauseSettingChange,
  onRetryDeviceSettings,
}) {
  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    onSettingChange(name, checked ? 1 : 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onSettingChange(name, value);
  };

  return (
    <div className="player-settings">
      <h3>Player Settings</h3>

      <div className="settings-section">
        <h4>Watermark Display</h4>
        <div className="watermark-display-setting">
          <div className="watermark-display-label">Watermark Size</div>
          <div className="watermark-display-options">
            <label>
              <input
                type="radio"
                name="watermark_size"
                value="small"
                checked={watermarkSize === 'small'}
                onChange={() => onWatermarkSizeChange('small')}
              />
              Small
            </label>
            <label>
              <input
                type="radio"
                name="watermark_size"
                value="medium"
                checked={watermarkSize === 'medium'}
                onChange={() => onWatermarkSizeChange('medium')}
              />
              Medium
            </label>
            <label>
              <input
                type="radio"
                name="watermark_size"
                value="large"
                checked={watermarkSize === 'large'}
                onChange={() => onWatermarkSizeChange('large')}
              />
              Large
            </label>
          </div>
        </div>
        <div className="watermark-display-setting">
          <div className="watermark-display-label">Display Position</div>
          <div className="watermark-display-options">
            <label>
              <input
                type="radio"
                name="watermark_position"
                value="top_right"
                checked={watermarkPosition === 'top_right'}
                onChange={() => onWatermarkPositionChange('top_right')}
              />
              Top Right
            </label>
            <label>
              <input
                type="radio"
                name="watermark_position"
                value="top_left"
                checked={watermarkPosition === 'top_left'}
                onChange={() => onWatermarkPositionChange('top_left')}
              />
              Top Left
            </label>
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className="settings-section">
        <h4>Display Settings</h4>
        <div className="setting-item">
          <label>Player Controls:</label>
          <select
            name="controls"
            value={playerSettings.controls}
            onChange={handleInputChange}
          >
            <option value="0">None</option>
            <option value="1">Default</option>
            <option value="2">Auto-Hide</option>
          </select>
        </div>
        <div className="setting-item">
          <label>Minimize Branding:</label>
          <input
            type="checkbox"
            name="modestbranding"
            checked={playerSettings.modestbranding === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>Fullscreen Option:</label>
          <input
            type="checkbox"
            name="fs"
            checked={playerSettings.fs === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>Progress Bar Color:</label>
          <select
            name="color"
            value={playerSettings.color}
            onChange={handleInputChange}
          >
            <option value="red">Red</option>
            <option value="white">White</option>
          </select>
        </div>
      </div>

      {/* Playback Settings */}
      <div className="settings-section">
        <h4>Playback Settings</h4>
        <div className="video-overlay-pause-setting">
          <label htmlFor="pause-background-video-on-video-overlay">
            <input
              id="pause-background-video-on-video-overlay"
              type="checkbox"
              checked={pauseBackgroundVideoOnVideoOverlay}
              disabled={overlayPauseSettingLoading || overlayPauseSettingSaving || overlayPauseSettingLoadFailed}
              onChange={(event) => onVideoOverlayPauseSettingChange(event.target.checked)}
              aria-describedby="video-overlay-pause-helper"
            />
            Pause background Video when Video Overlays are played
          </label>
          <p id="video-overlay-pause-helper">
            Pauses the current video and its audio while a video overlay is shown. Useful on devices with limited playback capacity.
          </p>
          {overlayPauseSettingLoading && <p role="status">Loading setting…</p>}
          {overlayPauseSettingSaving && <p role="status">Saving…</p>}
          {overlayPauseSettingError && <p role="alert">{overlayPauseSettingError}</p>}
          {overlayPauseSettingWarning && <p role="status">{overlayPauseSettingWarning}</p>}
          {overlayPauseSettingLoadFailed && <button type="button" onClick={onRetryDeviceSettings}>Retry</button>}
        </div>
        <div className="setting-item">
          <label>Autoplay:</label>
          <input
            type="checkbox"
            name="autoplay"
            checked={playerSettings.autoplay === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>Loop Video:</label>
          <input
            type="checkbox"
            name="loop"
            checked={playerSettings.loop === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>Play Inline on Mobile:</label>
          <input
            type="checkbox"
            name="playsinline"
            checked={playerSettings.playsinline === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>YouTube compatibility mode:</label>
          <select
            name="youtube_player_compatibility_mode"
            value={youtubePlayerCompatibilityMode || 'off'}
            onChange={(event) => onYoutubePlayerCompatibilityModeChange(event.target.value)}
          >
            <option value="off">Off</option>
            <option value="on">On</option>
            <option value="auto">Auto</option>
          </select>
        </div>
      </div>

      {/* Accessibility & Subtitles */}
      <div className="settings-section">
        <h4>Accessibility & Subtitles</h4>
        <div className="setting-item">
          <label>Show CC by Default:</label>
          <input
            type="checkbox"
            name="cc_load_policy"
            checked={playerSettings.cc_load_policy === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>CC Language:</label>
          <select
            name="cc_lang_pref"
            value={playerSettings.cc_lang_pref}
            onChange={handleInputChange}
          >
            {languageOptions.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Interaction Controls */}
      <div className="settings-section">
        <h4>Interaction Controls</h4>
        <div className="setting-item">
          <label>Disable Keyboard Controls:</label>
          <input
            type="checkbox"
            name="disablekb"
            checked={playerSettings.disablekb === 1}
            onChange={handleToggleChange}
          />
        </div>
        <div className="setting-item">
          <label>Video Annotations:</label>
          <select
            name="iv_load_policy"
            value={playerSettings.iv_load_policy}
            onChange={handleInputChange}
          >
            <option value="1">Show</option>
            <option value="3">Hide</option>
          </select>
        </div>
      </div>

      {/* Language Settings */}
      <div className="settings-section">
        <h4>Language Settings</h4>
        <div className="setting-item">
          <label>Interface Language:</label>
          <select
            name="hl"
            value={playerSettings.hl}
            onChange={handleInputChange}
          >
            {languageOptions.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="settings-section">
        <h4>Fade Settings</h4>
        <div className="setting-item">
          <label>Fade Duration (seconds):</label>
          <input
            type="number"
            min="0"
            max="10"
            value={fadeDuration}
            onChange={handleFadeDurationChange}
          />
        </div>
      </div>

    </div>
  );
}

export default PlayerSettings;
